import { useState } from 'react';
import { 
  IconButton, 
  Menu, 
  MenuItem, 
  Typography, 
  Divider, 
  Box,
  Fade 
} from '@mui/material';
import {
  // HelpOutlineOutlined as HelpIcon,
  ExpandMore as ExpandMoreIcon,
  MenuBook as DocsIcon,
  YouTube as YouTubeIcon,
  SupportAgent as SupportAgentIcon,
  // Support as SupportIcon,
  // Help as HelpCenterIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { YOUTUBE_CHANNEL_LINK } from '../common/AppConstants';

function SupportMenu() {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = (event) => {
    setAnchorEl(event.currentTarget);
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setAnchorEl(null);
    setIsHovered(false);
  };

  const handleClose = (path, isExternal = false) => {
    setAnchorEl(null);
    setIsHovered(false);
    if (path) {
      if (isExternal) {
        window.open(path, '_blank', 'noopener,noreferrer');
      } else {
        navigate(path);
      }
    }
  };

  const menuItems = [
    // {
    //   label: 'Help Center',
    //   icon: <HelpCenterIcon fontSize="small" />,
    //   path: '/help-center',
    //   description: 'Find answers to common questions'
    // },
    // { divider: true },
    {
      label: 'Support Center',
      icon: <SupportAgentIcon fontSize="small" />,
      path: '/support',
      description: 'Get personalized help'
    },
    {
      label: 'Video Tutorials',
      icon: <YouTubeIcon fontSize="small" />,
      path: YOUTUBE_CHANNEL_LINK,
      description: 'Watch step-by-step tutorials',
      isExternal: true
    },
    {
      label: 'Docs',
      icon: <DocsIcon fontSize="small" />,
      path: '/docs',
      description: 'Official documentation'
    },
  ];

  return (
    <>
      {/* Help Button with Text and Arrow */}
      <IconButton
        color="inherit"
        onMouseEnter={handleMouseEnter}
        sx={{
          pl: {xs: 1, sm: 2.5},
          pr: {xs: 0.5, sm: 2}, // 0.5 less for Expand Icon Right margin
          // py: 0.5,
          borderRadius: 2,
          transition: 'all 0.2s ease-in-out',
          backgroundColor: isHovered ? '#548681' : 'transparent',
          '&:hover': {
            backgroundColor: '#548681',
            transform: 'translateY(-1px)',
          }
        }}
      >
        {/* Box: visible on md+ (desktop/tablet) */}
        <Box sx={{ 
          display: { xs: 'none', md: 'flex' },
          alignItems: 'center', 
          gap: 0.5 
        }}>
          {/* <HelpIcon sx={{ fontSize: 24 }} /> */}
          <Typography 
            variant="h4"
            sx={{
              // fontWeight: 500,
              // fontSize: '0.875rem',
              // lineHeight: 1
              fontSize: {
                xs: '0.87rem',  // e.g., smaller on mobile
                sm: '1.1rem' // larger on tablet+
              },
            }}
          >
            Need Help ?
          </Typography>
          <ExpandMoreIcon 
            sx={{ 
              fontSize: 24,
              transform: isHovered ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s ease-in-out'
            }} 
          />
        </Box>

        {/* Icon: visible only on mobile (xs) */}
        <SupportAgentIcon
          sx={{
            display: { xs: 'inline-flex', md: 'none' },
          }}
        />
      </IconButton>

      {/* Enhanced Dropdown Menu */}
      <Menu
        id="support-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => handleClose(null)}
        TransitionComponent={Fade}
        transitionDuration={200}
        sx={{
          mt: 2,
          '& .MuiPaper-root': {
            minWidth: 240,
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            backgroundColor: 'rgba(255, 255, 255, 0.95)',
            py: 1,
          },
        }}
        MenuListProps={{
          onMouseLeave: handleMouseLeave,
          sx: { py: 0 }
        }}
        disableAutoFocusItem={true}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        {menuItems.map((item, index) => 
          item.divider ? (
            <Divider 
              key={index} 
              sx={{ 
                my: 1,
                borderColor: 'rgba(0, 0, 0, 0.08)'
              }} 
            />
          ) : (
            <MenuItem
              key={item.label}
              onClick={() => handleClose(item.path, item.isExternal)}
              sx={{
                px: 2,
                py: 1.5,
                mx: 1,
                borderRadius: 2,
                transition: 'all 0.15s ease-in-out',
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1.5,
                width: '100%'
              }}>
                <Box sx={{ 
                  color: item.isExternal ? '#FF0000' : 'primary.main',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {item.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 500,
                      color: 'text.primary',
                      mb: 0.25
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      color: 'text.secondary',
                      fontSize: '0.75rem',
                      lineHeight: 1.2
                    }}
                  >
                    {item.description}
                  </Typography>
                </Box>
              </Box>
            </MenuItem>
          )
        )}
      </Menu>
    </>
  );
}

export default SupportMenu;
